/**
 * iter832 — Compact POS Settings gear.
 * A single button in the POS header that opens a popover with every
 * per-device toggle (Density, Kitchen tickets, plus room for future
 * ones). Replaces the pile of individual pill toggles that were
 * cluttering the header. All state lives in the parent — this
 * component is purely presentation + click-handling.
 */
import { useState } from 'react';
import { Settings, LayoutGrid, Rows3, ChefHat, Check, X, Printer, ClipboardList, ScanLine, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { formatBreadcrumbsAsText, clearBreadcrumbs } from '@/lib/posBreadcrumbs';

export function PosSettingsButton({ density, onToggleDensity, kitchenPrinting, onToggleKitchenPrinting, autoPrint, onToggleAutoPrint, onAddExpense, toolbarHidden = [], onToggleToolbarButton, canEditToolbar = false }) {
  const [open, setOpen] = useState(false);
  const TOOLBAR_BTNS = [
    { key: 'print', label: 'Print' }, { key: 'reprint', label: 'Reprint Last' },
    { key: 'kitchen', label: 'Kitchen' }, { key: 'void', label: 'Void' },
    { key: 'discount', label: 'Discount' }, { key: 'notes', label: 'Notes' },
    { key: 'tags', label: 'Tags' },
  ];
  const isComfortable = density === 'comfortable';
  // iter958 — per-device stock-request chime mute (self-contained).
  const [chimeMuted, setChimeMuted] = useState(() => {
    try { return localStorage.getItem('pos_stock_chime_muted') === '1'; } catch { return false; }
  });
  const toggleChime = () => {
    setChimeMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem('pos_stock_chime_muted', next ? '1' : '0'); } catch { /* ignore */ }
      toast.success(next ? 'Stock request chime muted' : 'Stock request chime on');
      return next;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-10 w-10 p-0 rounded-xl"
          title="POS device settings"
          data-testid="pos-settings-btn"
        >
          <Settings size={15} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="pos-settings-popover">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-xs font-bold uppercase tracking-wide">POS Settings</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Per-device — saved on this tablet</div>
        </div>
        <div className="p-2 space-y-1">
          {/* iter924 — Quick bill capture (moved from the main POS screen).
              Opens the existing /scan-invoice QR page that auto-fills
              Vendor, VAT and Amount. */}
          {onAddExpense && (
            <button
              type="button"
              className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
              onClick={() => { setOpen(false); onAddExpense(); }}
              data-testid="pos-settings-add-expense"
            >
              <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center">
                <ScanLine size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">Add Bill/Expense</div>
                <div className="text-[10px] text-muted-foreground">Scan a supplier QR — vendor, VAT & amount auto-fill</div>
              </div>
            </button>
          )}
          {/* Density */}
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
            onClick={onToggleDensity}
            data-testid="pos-settings-density"
          >
            <div className="w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center">
              {isComfortable ? <Rows3 size={14} /> : <LayoutGrid size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Menu density</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {isComfortable ? 'Comfortable — bigger tiles' : 'Compact — more items per row'}
              </div>
            </div>
            <span className="text-[10px] font-semibold text-primary shrink-0">
              {isComfortable ? 'Comfy' : 'Compact'}
            </span>
          </button>

          {/* Kitchen Printing */}
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
            onClick={onToggleKitchenPrinting}
            data-testid="pos-settings-kot"
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${kitchenPrinting ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500'}`}>
              <ChefHat size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Kitchen tickets (KOT)</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {kitchenPrinting
                  ? 'On — print both receipt & kitchen ticket'
                  : 'Off — print only customer receipt'}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold shrink-0 ${kitchenPrinting ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500'}`}>
              {kitchenPrinting ? <><Check size={11} /> On</> : <><X size={11} /> Off</>}
            </span>
          </button>

          {/* iter833 — Auto-print receipt after payment */}
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
            onClick={onToggleAutoPrint}
            data-testid="pos-settings-auto-print"
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${autoPrint ? 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500'}`}>
              <Printer size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Auto-print receipt</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {autoPrint
                  ? 'On — receipt prints immediately after payment'
                  : 'Off — show receipt preview dialog first'}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold shrink-0 ${autoPrint ? 'text-orange-700 dark:text-orange-300' : 'text-slate-500'}`}>
              {autoPrint ? <><Check size={11} /> On</> : <><X size={11} /> Off</>}
            </span>
          </button>

          {/* iter958 — Stock-request chime mute (per device) */}
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
            onClick={toggleChime}
            data-testid="pos-settings-chime"
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${chimeMuted ? 'bg-slate-100 dark:bg-slate-800/60 text-slate-500' : 'bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300'}`}>
              {chimeMuted ? <BellOff size={14} /> : <Bell size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Stock request chime</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {chimeMuted ? 'Muted — no sound on new stock requests' : 'On — plays a chime for new stock requests'}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold shrink-0 ${chimeMuted ? 'text-slate-500' : 'text-sky-700 dark:text-sky-300'}`}>
              {chimeMuted ? <><X size={11} /> Off</> : <><Check size={11} /> On</>}
            </span>
          </button>

          {/* iter961 — Toolbar button ON/OFF (branch-specific, DB-backed via
              /pos/toolbar-config). Only admins/managers can change them. */}
          {onToggleToolbarButton && (
            <div className="pt-1 mt-1 border-t border-border">
              <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                Toolbar buttons {canEditToolbar ? '· this branch' : '· managers only'}
              </div>
              {TOOLBAR_BTNS.map(({ key, label }) => {
                const on = !toolbarHidden.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!canEditToolbar}
                    onClick={() => onToggleToolbarButton(key)}
                    className="w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed text-left transition-colors"
                    data-testid={`pos-settings-toolbar-${key}`}
                  >
                    <span className="text-xs font-medium">{label}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${on ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-400'}`}>
                      {on ? <><Check size={11} /> On</> : <><X size={11} /> Off</>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* iter845d — Diagnostic breadcrumb log. Copies the exact
              step-by-step sequence of checkout / print events to the
              clipboard so it can be pasted into a support ticket. */}
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/60 transition-colors text-left"
            onClick={async () => {
              const txt = formatBreadcrumbsAsText();
              try {
                if (navigator?.clipboard?.writeText) {
                  await navigator.clipboard.writeText(txt);
                  toast.success('Diagnostic log copied — paste into a support ticket');
                } else {
                  // Fallback: prompt-style so cashier can long-press → copy.
                  window.prompt('POS diagnostic log — copy this text:', txt);
                }
              } catch {
                window.prompt('POS diagnostic log — copy this text:', txt);
              }
              setOpen(false);
            }}
            data-testid="pos-settings-diag-log"
          >
            <div className="w-7 h-7 rounded-lg bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 flex items-center justify-center">
              <ClipboardList size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Copy diagnostic log</div>
              <div className="text-[10px] text-muted-foreground truncate">
                Last ~200 checkout / print events for support
              </div>
            </div>
            <span className="text-[10px] font-semibold text-sky-700 dark:text-sky-300 shrink-0">Copy</span>
          </button>
          {/* Small "clear" affordance right beneath the copy button. */}
          <button
            type="button"
            className="w-full text-[10px] text-muted-foreground hover:text-error text-right px-2.5 py-1 transition-colors"
            onClick={() => { clearBreadcrumbs(); toast.info('Diagnostic log cleared'); }}
            data-testid="pos-settings-diag-clear"
          >
            clear log
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PosSettingsButton;
